import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { WishList } from '../interfaces/wish-list-items.interface';
import { renderWishListItems } from '../utils/wish-list.util';
import { UsePaginationInMemory } from '../../pagination/in-memory/use-pagination-in-memory.interface';
import { getPaginationDetails, getPaginationMessage } from '../../pagination/utils/pagination.utils';
import { html, PropertyValues } from 'lit';
import { Item } from '../../../../dist/esm/features/wish-list/interfaces/wish-list-items.interface';

const MAX_ITEMS_PAGE = 10;

@customElement('mhecomm-paginated-wish-list-items')
export class PaginatedWishListItems extends connect(store)(BaseComponent) implements UsePaginationInMemory {
  @property({ type: Object })
  wishList!: WishList;

  @property({ type: Boolean })
  isReadOnly = false;

  @property({ type: Number })
  pageSize = MAX_ITEMS_PAGE;

  @state()
  private currentPage = 0;

  @state()
  private firstOfPagination = 1;

  @state()
  private lastOfPagination = 1;

  async connectedCallback() {
    super.connectedCallback();
    this.shadowRoot?.addEventListener('go-to-page-in-memory', this.handlePageChange.bind(this));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.shadowRoot?.removeEventListener('go-to-page-in-memory', this.handlePageChange.bind(this));
  }

  protected updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('wishList')) {
      this.updatePaginationData();
    }
  }

  /**
   * It should be used when the pagination occurs on the pagination-in-memory
   * or when some wishlist is removed, because we might need to update the results
   * based on the new total wish lists
   *
   * @param paginationNumber - The page to navigate for or the current page if
   * a wish list was removed
   */
  updatePaginationData(paginationNumber?: number) {
    const page = paginationNumber ? paginationNumber - 1 : this.currentPage;
    const { currentPage, firstOfPage, lastOfPage } = getPaginationDetails(
      page,
      this.pageSize,
      this.wishList.wishlist_items.length
    );
    this.firstOfPagination = firstOfPage;
    this.lastOfPagination = lastOfPage;
    this.currentPage = currentPage;
  }

  handlePageChange(e: CustomEvent) {
    this.updatePaginationData(e.detail.paginationNumber);
  }

  getItemsToRender(): Item[] {
    const items: Item[] = [];
    for (let i = this.firstOfPagination - 1; i < this.lastOfPagination; i++) {
      this.wishList.wishlist_items[i] && items.push(this.wishList.wishlist_items[i]);
    }
    return items;
  }

  renderPaginationMsg() {
    return html`
      <mhecomm-pagination-message
        ?useRawMessage=${true}
        message="${getPaginationMessage(this.currentPage, this.pageSize, this.wishList.wishlist_items.length)}"
        id="pagination_message"
        data-automation-id="pagination_message"
      >
      </mhecomm-pagination-message>
    `;
  }

  renderPaginationActions() {
    return html`
      <div class="mt-4">
        <mhecomm-pagination-in-memory
          .currentPage=${this.currentPage}
          maxPagesShown="3"
          .total=${this.wishList.wishlist_items.length}
          .pageLength=${this.pageSize}
        ></mhecomm-pagination-in-memory>
      </div>
    `;
  }

  renderComp() {
    return html` ${this.renderPaginationMsg()}
    ${renderWishListItems(
      {
        ...this.wishList,
        wishlist_items: this.getItemsToRender(),
      } as WishList,
      this.isReadOnly
    )}
    ${this.renderPaginationActions()}`;
  }
}
